import { useRef, type ReactNode } from 'react';
import {
  Button,
  Card,
  Disclosure,
  Input,
  InputGroup,
  ListBox,
  Select,
  Spinner,
  TextField,
} from '@heroui/react';
import { Trash2, Upload } from 'lucide-react';
import { ColorPicker } from '@/components/molecules/ColorPicker';
import { MarkdownEditor } from '@/components/molecules/MarkdownEditor';
import { MarkdownTextarea } from '@/components/molecules/MarkdownTextarea';
import { FieldLabel } from '@/components/molecules/FieldLabel';
import type { Client, TemplatePhoto, VisitTemplate } from '@/lib/types';
import type { TemplateFormValues } from '../useTemplateEditPage';

interface TemplateEditProps {
  form: TemplateFormValues;
  setField: <K extends keyof TemplateFormValues>(key: K, value: TemplateFormValues[K]) => void;
  /** Ошибка названия с бэка (409 — дубль в рамках клиента) */
  titleError?: string | null;
  /** Просмотр: значения — простым текстом, без полей ввода */
  readOnly?: boolean;
  isStaff: boolean;
  clients: Client[];
  /** Запущенные шаблоны для селекта цепочки (кроме себя) */
  chainTemplates: VisitTemplate[];
  /** Названия связанных сущностей для readonly-режима селектов */
  clientTitle?: string | null;
  nextTemplateTitle?: string | null;
  /** Slug существующего шаблона (readonly-строка в «Дополнительно») */
  slug?: string | null;
  photos: TemplatePhoto[];
  /** Черновик или несохранённый (new): фото можно грузить/удалять */
  isDraft: boolean;
  isNew: boolean;
  isUploadingPhoto?: boolean;
  onUploadPhoto: (file: File) => void;
  onDeletePhoto: (photoId: string) => void;
}

/** Подпись контрола в карточке шаблона — дефолтный стиль HeroUI `.label`
    (text-sm medium foreground), как у лейблов markdown-полей. Настоящий
    Label (ассоциируется с контролом через TextField/Select), компактный
    muted-вариант FieldLabel рядом с 14px-значениями растворяется */
function TemplateFieldLabel({ children }: { children: ReactNode }) {
  return <FieldLabel className="text-sm text-foreground">{children}</FieldLabel>;
}

/** Строка просмотра: заголовок строки + значение простым текстом — вместо
    disabled-инпутов. Заголовок — просто span: настоящий Label уместен
    только у контрола, а в просмотре его нет */
function ViewRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-sm font-medium text-foreground">{label}</span>
      <div className="min-h-6 text-sm text-foreground">{children}</div>
    </div>
  );
}

/**
 * Главная карточка формы шаблона: название, markdown-поля, фото инструкции
 * и строка «стоимость + цвет». В просмотре (readOnly) значения рендерятся
 * текстом, подписи полей сохраняются. WYSIWYG (MDXEditor) — только у
 * инструкции; остальные поля правятся как исходник. «Дополнительно» —
 * расхлопывающаяся карточка: follow-up, ИИ-контекст, привязки, slug.
 */
export function TemplateEdit({
  form,
  setField,
  titleError,
  readOnly = false,
  isStaff,
  clients,
  chainTemplates,
  clientTitle,
  nextTemplateTitle,
  slug,
  photos,
  isDraft,
  isNew,
  isUploadingPhoto = false,
  onUploadPhoto,
  onDeletePhoto,
}: TemplateEditProps) {
  const photoInputRef = useRef<HTMLInputElement>(null);
  // Правки фото — до запуска: new|draft (guard на бэке), просмотр — без действий
  const canManagePhotos = !readOnly && (isDraft || isNew);
  // В просмотре блок имеет смысл только с фото; у запущенного шаблона
  // без фото — прячем
  const showPhotosBlock = canManagePhotos || photos.length > 0;
  // Просмотр стоимости: «699 ₽» вместо сырой строки «699.00» в инпуте
  const costNumber = Number(form.cost);
  const costText =
    form.cost.trim() !== '' && Number.isFinite(costNumber)
      ? `${costNumber.toLocaleString('ru-RU')} ₽`
      : '—';

  return (
    <>
      <Card className="bg-surface">
        <Card.Header>
          <Card.Title>Основная информация</Card.Title>
        </Card.Header>
        <Card.Content className="flex flex-col gap-4">
          {readOnly ? (
            <ViewRow label="Название">{form.title || '—'}</ViewRow>
          ) : (
            <div className="flex flex-col gap-1">
              <TextField
                variant="secondary"
                value={form.title}
                onChange={(v) => setField('title', v)}
                isRequired
                isInvalid={Boolean(titleError)}
              >
                <TemplateFieldLabel>Название</TemplateFieldLabel>
                <Input placeholder="Например: Проверка выкладки" />
              </TextField>
              {/* Слот ошибки фиксированной высоты: появление сообщения
                  не сдвигает поля ниже */}
              <p
                className="min-h-5 text-sm text-danger"
                aria-live={titleError ? 'polite' : undefined}
              >
                {titleError ?? ''}
              </p>
            </div>
          )}

          {/* Описание/follow-up/ИИ-контекст — markdown как исходник (textarea);
              WYSIWYG — только у инструкции ниже */}
          <MarkdownTextarea
            label="Описание"
            value={form.description}
            onChange={(v) => setField('description', v)}
            placeholder="Краткое описание шаблона"
            minHeight={72}
            readOnly={readOnly}
          />

          {/* Инструкция и её фото-иллюстрации; живое мобильное превью —
              корпусом телефона в правой колонке страницы */}
          <MarkdownEditor
            label="Инструкция для исполнителя"
            value={form.instruction}
            onChange={(v) => setField('instruction', v)}
            placeholder="Пошаговая инструкция для исполнителя"
            readOnly={readOnly}
          />

          {/* Фото инструкции: иллюстрации в мобильном приложении.
              Первое фото — обложка превью */}
          {showPhotosBlock && (
            <div className="flex flex-col gap-2">
              <span className="text-sm font-medium text-foreground">Фото инструкции</span>
              {photos.length > 0 && (
                <div className="flex flex-wrap gap-3">
                  {photos.map((photo) => (
                    <div key={photo.id} className="relative">
                      <img
                        src={photo.url}
                        alt={photo.url}
                        className="h-20 w-28 rounded-[var(--radius)] border border-[var(--border)] object-cover"
                      />
                      {canManagePhotos && (
                        <Button
                          variant="danger-soft"
                          size="sm"
                          className="absolute -right-2 -top-2"
                          aria-label="Удалить фото"
                          onPress={() => onDeletePhoto(photo.id)}
                        >
                          <Trash2 className="size-3.5" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {canManagePhotos && (
                <>
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) onUploadPhoto(file);
                      e.target.value = '';
                    }}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    className="self-start"
                    onPress={() => photoInputRef.current?.click()}
                    isDisabled={isUploadingPhoto}
                  >
                    {isUploadingPhoto ? <Spinner size="sm" /> : <Upload className="size-4" />}
                    Загрузить фото
                  </Button>
                </>
              )}
            </div>
          )}

          {/* Стоимость + цвет — один ряд из двух полей: та же система
              «подпись сверху → поле снизу», что у остальных полей карточки */}
          <div className="flex flex-wrap gap-x-8 gap-y-4">
            {readOnly ? (
              <ViewRow label="Стоимость">{costText}</ViewRow>
            ) : (
              <div className="flex flex-col gap-1.5">
                {/* span, не Label: InputGroup — не RAC-поле, ассоциации
                    не будет; инпут подписан через aria-label */}
                <span className="text-sm font-medium text-foreground">Стоимость</span>
                {/* Спиннеры скрыты: суммы печатают вручную, а рядом
                    суффикс «₽» */}
                <InputGroup variant="secondary" className="w-36">
                  <InputGroup.Input
                    type="number"
                    min={0}
                    step="0.01"
                    value={form.cost}
                    onChange={(e) => setField('cost', e.target.value)}
                    placeholder="0"
                    aria-label="Стоимость"
                    className="[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                  />
                  <InputGroup.Suffix>₽</InputGroup.Suffix>
                </InputGroup>
              </div>
            )}
            <ColorPicker
              variant="field"
              value={form.color}
              onChange={readOnly ? undefined : (v) => setField('color', v)}
              readOnly={readOnly}
            />
          </div>
        </Card.Content>
      </Card>

      <Card className="bg-surface">
        <Disclosure>
          <Disclosure.Heading>
            <Disclosure.Trigger className="flex w-full items-center justify-between gap-2 py-1 text-left transition-colors">
              <span className="text-sm leading-6 font-medium text-foreground">Дополнительно</span>
              <Disclosure.Indicator className="text-muted" />
            </Disclosure.Trigger>
          </Disclosure.Heading>
          <Disclosure.Content>
            <div className="flex flex-col gap-4 border-t border-border pt-4">
              <MarkdownTextarea
                label="Инструкция после выполнения визита"
                value={form.followUp}
                onChange={(v) => setField('followUp', v)}
                placeholder="Текст, показываемый перед отправкой данных визита"
                minHeight={72}
                readOnly={readOnly}
              />
              <MarkdownTextarea
                label="ИИ-контекст"
                value={form.aiContext}
                onChange={(v) => setField('aiContext', v)}
                placeholder="Общий контекст для ИИ-анализа всего визита"
                minHeight={72}
                readOnly={readOnly}
              />

              {isStaff &&
                (readOnly ? (
                  <ViewRow label="Клиент">{clientTitle ?? 'Платформенный'}</ViewRow>
                ) : (
                  <Select
                    className="w-full"
                    variant="secondary"
                    value={form.clientId || 'none'}
                    onChange={(key) =>
                      setField('clientId', key === 'none' || key === null ? '' : String(key))
                    }
                  >
                    <TemplateFieldLabel>Клиент</TemplateFieldLabel>
                    <Select.Trigger>
                      <Select.Value />
                      <Select.Indicator />
                    </Select.Trigger>
                    <Select.Popover>
                      <ListBox>
                        <ListBox.Item key="none" id="none" textValue="— без привязки —">
                          — без привязки —
                          <ListBox.ItemIndicator />
                        </ListBox.Item>
                        {clients.map((c) => (
                          <ListBox.Item key={c.id} id={c.id} textValue={c.title}>
                            {c.title}
                            <ListBox.ItemIndicator />
                          </ListBox.Item>
                        ))}
                      </ListBox>
                    </Select.Popover>
                  </Select>
                ))}

              {isStaff &&
                (readOnly ? (
                  <ViewRow label="Следующий шаблон (цепочка)">{nextTemplateTitle ?? '—'}</ViewRow>
                ) : (
                  <Select
                    className="w-full"
                    variant="secondary"
                    value={form.nextVisitTemplateId || 'none'}
                    onChange={(key) =>
                      setField(
                        'nextVisitTemplateId',
                        key === 'none' || key === null ? '' : String(key),
                      )
                    }
                  >
                    <TemplateFieldLabel>Следующий шаблон (цепочка)</TemplateFieldLabel>
                    <Select.Trigger>
                      <Select.Value />
                      <Select.Indicator />
                    </Select.Trigger>
                    <Select.Popover>
                      <ListBox>
                        <ListBox.Item key="none" id="none" textValue="—">
                          —
                          <ListBox.ItemIndicator />
                        </ListBox.Item>
                        {chainTemplates.map((t) => (
                          <ListBox.Item key={t.id} id={t.id} textValue={t.title}>
                            {t.title}
                            <ListBox.ItemIndicator />
                          </ListBox.Item>
                        ))}
                      </ListBox>
                    </Select.Popover>
                  </Select>
                ))}

              {readOnly && slug && (
                <ViewRow label="Slug">
                  <span className="font-mono text-xs">{slug}</span>
                </ViewRow>
              )}
            </div>
          </Disclosure.Content>
        </Disclosure>
      </Card>
    </>
  );
}
